import React, { useState, useEffect, useMemo } from 'react';
import { 
    Dialog, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box, 
    IconButton, 
    InputAdornment,
    FormHelperText
} from '@mui/material';
import { toast } from 'react-toastify';
import { addStops, addSTMSStops, getVehicle } from './action.ts'; 
import MapModal from '../Warehouses/map-modal.tsx'; 
import Dropdown from '../../Components/DropDown/drop-down.tsx'; 
import Heading from '../../Components/Heading/heading.tsx';
import mapsIcon from '../../../assets/map.png';

interface AddStopsModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void; 
    companyOptions: { id: string; label: string }[]; 
}

const AddStopsModal: React.FC<AddStopsModalProps> = ({ open, onClose, onSuccess, companyOptions }) => {
    const [name, setName] = useState('');
    const [companiesId, setCompaniesId] = useState(''); 
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [radius, setRadius] = useState(''); 
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [vehicles, setVehicles] = useState<{ id: string; label: string }[]>([]);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [companyError, setCompanyError] = useState('');
    const [vehicleError, setVehicleError] = useState('');
    const [nameError, setNameError] = useState('');
    const [latError, setLatError] = useState('');
    const [lngError, setLngError] = useState('');
    const [radiusError, setRadiusError] = useState('');

    const isStmsSelected = useMemo(() => {
        const selectedCompany = companyOptions?.find(c => String(c.id) === String(companiesId));
        return selectedCompany?.label?.toLowerCase() === 'stms';
    }, [companiesId, companyOptions]);

    useEffect(() => {
        if (isStmsSelected) {
            getVehicle(
                100, 
                0,    
                (response: any) => {
                    if (response?.status === 200 && response?.message?.vehicles_list) {
                        const vehicleOptions = response.message.vehicles_list.map((v: any) => ({
                            id: String(v.id), 
                            label: String(v.number_plate) 
                        }));
                        setVehicles(vehicleOptions);
                    }
                },
                (errorData: any) => {
                    toast.error(errorData?.message || "Failed to fetch vehicles");
                },
                {active:1},        
                '', '', '', '', companiesId 
            );
        } else {
            setVehicles([]);
            setSelectedVehicle('');
            setVehicleError('');
        }
    }, [isStmsSelected, companiesId]);

    const handleLocationSelect = (selectedAddress: string, selectedLat: string, selectedLng: string) => {
        setLat(selectedLat);
        setLatError('');
        setLng(selectedLng);
        setLngError('');
    };

    const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRadius(value);

        if (value.trim() === '') {
            setRadiusError('Radius is required');
            return;
        }

        const hasInvalidChars = /[^\d.]/.test(value); 
        const dotCount = (value.match(/\./g) || []).length; 

        if (hasInvalidChars) {
            setRadiusError('Alphabets and special characters are not allowed');
        } else if (dotCount > 1) {
            setRadiusError('Only one decimal point is allowed');
        } else {
            setRadiusError('');
        }
    };

    const handleModalClose = () => {
        setCompanyError('');
        setVehicleError('');
        setNameError('');
        setLatError('');
        setLngError('');
        setRadiusError('');
        onClose();
    };

    const handleSubmit = () => {
        let isValid = true;
        if (!companiesId) {
            setCompanyError("Company is required");
            isValid = false;
        }
        if (isStmsSelected && !selectedVehicle) {
            setVehicleError("Vehicle is required");
            isValid = false;
        }
        if (!name.trim()) {
            setNameError("Stop Name is required");
            isValid = false;
        }
        if (!lat.trim()) {
            setLatError("Latitude is required");
            isValid = false;
        }
        if (!lng.trim()) {
            setLngError("Longitude is required");
            isValid = false;
        }
        if (!radius.trim()) {
            setRadiusError("Radius is required");
            isValid = false;
        } else if (radiusError) {
            isValid = false;
        }

        if (!isValid) return;

        const radiusValue = Number(radius);
        if (isNaN(radiusValue)) {
            setRadiusError("Radius must be a valid number");
            return;
        }

        const handleSuccess = (response: any) => {
            if (response.status === 200 || response.status === 201) {
                toast.success("Stop added successfully");
                
                setName('');
                setCompaniesId('');
                setLat('');
                setLng('');
                setRadius('');
                setSelectedVehicle('');
                handleModalClose();
                if (onSuccess) onSuccess(); 
            } else {
                toast.error(response.message || "Failed to add stop");
            }
        };

        const handleError = (errorMessage: string) => toast.error(errorMessage);
        if (isStmsSelected) {
            addSTMSStops(
                name,
                companiesId, 
                lat,
                lng,
                radiusValue, 
                handleSuccess,
                handleError,
                selectedVehicle
            );
        } else {
            addStops(
                name,
                companiesId, 
                lat,
                lng,
                radiusValue, 
                handleSuccess,
                handleError,
            );
        }
    };

    const initialLocation = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : undefined;

    return (
        <>
            <Dialog open={open} onClose={handleModalClose} maxWidth="sm" >
                <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>    
                    <Heading title="Add Stops" closeIcon="true" onClose={handleModalClose} />
                </Box>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        
                        <Box sx={{ width: '210px' }}>
                            <Dropdown 
                                options={companyOptions ? [...companyOptions].sort((a, b) => a.label.localeCompare(b.label)) : []}                
                                selectedValue={companiesId} 
                                handleChange={(e: any) => {
                                    setCompaniesId(String(e.target.value));
                                    setCompanyError('');
                                }} 
                                label="Company *"
                                placeholder="Select Company"
                                width="210px" 
                                height="40px"
                                searchable={true}
                            />
                            {companyError && <FormHelperText error sx={{ ml: 1 }}>{companyError}</FormHelperText>}
                        </Box>
                        
                        {isStmsSelected && (
                            <Box sx={{ width: '210px' }}>
                                <Dropdown 
                                    options={vehicles}                
                                    selectedValue={selectedVehicle} 
                                    handleChange={(e: any) => {
                                        setSelectedVehicle(String(e.target.value));
                                        console.log('Selected Vehicle:', e.target.value); 
                                        setVehicleError('');
                                    }} 
                                    label="Vehicle Number *"
                                    placeholder="Select Vehicle"
                                    width="210px" 
                                    height="40px"
                                />
                                {vehicleError && <FormHelperText error sx={{ ml: 1 }}>{vehicleError}</FormHelperText>}
                            </Box>
                        )}
                        
                        <TextField 
                            label="Stop Name" 
                            required
                            value={name} 
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameError('');
                            }} 
                            error={!!nameError}
                            helperText={nameError}
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                        
                        <TextField 
                            label="Latitude" 
                            required
                            value={lat} 
                            onChange={(e) => {
                                setLat(e.target.value);
                                setLatError('');
                            }}
                            error={!!latError}
                            helperText={latError}
                            placeholder="Auto-filled from map"
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                        
                        <TextField 
                            label="Longitude"
                            required 
                            value={lng} 
                            onChange={(e) => {
                                setLng(e.target.value);
                                setLngError('');
                            }}
                            error={!!lngError}
                            helperText={lngError}
                            placeholder="Auto-filled from map"
                            sx={{ width: '210px' }}
                            size="small" 
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            onClick={() => setIsMapOpen(true)} 
                                            color="primary" edge="end" title="Select location on map"
                                        >
                                            <img src={mapsIcon} alt="Map View" style={{ width: '30px', height: '30px' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }} 
                        />

                        <TextField 
                            label="Radius (meters)" 
                            required
                            value={radius} 
                            onChange={handleRadiusChange}
                            type="text"
                            error={!!radiusError} 
                            helperText={radiusError} 
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                    </Box>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleModalClose} className="custom-cancel-button">Cancel</Button>
                    <Button onClick={handleSubmit} className="custom-button" variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <MapModal 
                open={isMapOpen} 
                onClose={() => setIsMapOpen(false)} 
                onLocationSelect={handleLocationSelect}
                initialLocation={initialLocation}
            />
        </>
    );
};

export default AddStopsModal;